// addModal.js
'use strict';
import { List } from 'immutable';
import {
  ADD_MODAL,
  ADD_MODAL_NEXT_ID,
  SET_MODAL_STATUS,
} from '../ActionTypes.js';
import { modalTemplate } from '../reducer/modals.js';
import { ANIMATION_DURATION_MSEC } from '../resource/modalConstants.js';

const defaultList = new List();

/**
 * Add modal
 * @kind action
 * @param {string} {id} - action param.
 * @param {string} [{prevId}] - previous modal id.
 * @param {array} [{higherThanIds}=[]] - model ids which this modal should higher than.
 * @param {array} [{lowerThanIds}=[]] - model ids which this modal should lower than.
 * @param {bool} [{isHigherThanAll}] - should modal higher than all existing modals.
 * @param {bool} [{isLowerThanAll}] - should modal lower than all existing modals.
 * @param {string} [{transitionStatus}] - transition status.
 * @return {Promise} Action promise.
 */
const addModal =
  ({
    id,
    prevId,
    higherThanIds = [],
    lowerThanIds = [],
    isHigherThanAll,
    isLowerThanAll,
    transitionStatus,
    payload,
  }) =>
  async (dispatch, getState) => {
    const modals = getState().get('modals') || defaultList;

    let shouldComputeHigher = true;
    if (
      !(higherThanIds.length || isHigherThanAll) &&
      (isLowerThanAll || lowerThanIds.length)
    ) {
      shouldComputeHigher = false;
    }

    const referenceIds = shouldComputeHigher
      ? [
          ...new Set(
            [
              prevId,
              ...higherThanIds,
              ...(isHigherThanAll ? [...modals.keys()] : []),
            ].filter(id => id)
          ),
        ]
      : [
          ...new Set(
            [
              prevId,
              ...lowerThanIds,
              ...(isLowerThanAll ? [...modals.keys()] : []),
            ].filter(id => id)
          ),
        ];

    const referenceZIndexes = modals
      .filter(modal => referenceIds.includes(modal.get('id')))
      .map(modal => modal.get('zIndex'))
      .toArray();

    const zIndex = shouldComputeHigher
      ? Math.max(...referenceZIndexes, 99) + 1
      : referenceZIndexes.length
        ? Math.min(...referenceZIndexes) - 1
        : 99;

    const prevModal = modals.get(prevId);
    if (prevModal) {
      dispatch({
        type: ADD_MODAL_NEXT_ID,
        payload: { id: prevId, nextId: id },
      });
    }

    const modalData = {
      ...modalTemplate,
      higherThanIds: shouldComputeHigher ? referenceIds : [],
      lowerThanIds: !shouldComputeHigher ? referenceIds : [],
      id,
      zIndex,
      payload,
    };

    if (transitionStatus) {
      setTimeout(() => {
        // Skip if status changed (e.g. closed mid-opening) to avoid overwriting back to `display`.
        const currentModal = getState().get('modals')?.get(id);
        if (!currentModal || currentModal.get('status') !== transitionStatus) {
          return;
        }
        dispatch({
          type: SET_MODAL_STATUS,
          payload: { status: modalTemplate.status, id },
        });
      }, ANIMATION_DURATION_MSEC); // TODO: remote config
      modalData.status = transitionStatus;
    }

    return dispatch({ type: ADD_MODAL, payload: modalData });
  };

export default addModal;
